import React, { useContext, useRef, useState } from 'react';
import { Marker, Popup, Tooltip } from 'react-leaflet';

import { FuelButton, FuelIcon, FuelTypography } from '@frete.com/fuel-react';
import { occurrenceRead } from '@services/journeys';
import AppContext from '@context';
import { MarkerInterface } from '../MapContainerType';
import { iconsLocations } from './LocationMarkersContants';
import * as Styled from './LocationMarkersStyles';
import { IMarkerWithPopup } from './LocationMarkersTypes';

function MarkerWithPopup({
  icon,
  read = false,
  label,
  position,
  journeyId,
  description,
  occurrenceId,
}: IMarkerWithPopup) {
  const popupRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { dispatch } = useContext(AppContext);

  function handleOccurrenceResolved() {
    if (!occurrenceId) return;
    if (!isLoading) {
      setIsLoading(true);
      occurrenceRead(occurrenceId)
        .then(() => {
          dispatch({
            type: 'journey_occurrence_read',
            payload: {
              journeyId,
              read: true,
            },
          });
          window.dispatchEvent(
            new CustomEvent('notificationShow', {
              detail: {
                isShow: true,
                heading: 'Ocorrência resolvida com sucesso!',
                severity: 'success',
              },
            }),
          );
        })
        .catch(() => {
          window.dispatchEvent(
            new CustomEvent('notificationShow', {
              detail: {
                isShow: true,
                heading: 'Sua foi possível realizar esta ação',
                message: 'Tente novamente mais tarde.',
                severity: 'error',
              },
            }),
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  const handleClose = () => {
    const marker = popupRef.current;
    if (marker) {
      marker.closePopup();
    }
  };

  return (
    <Marker
      position={{ lat: position.lat, lng: position.lon }}
      icon={icon}
      ref={popupRef}
    >
      {label && description && !read && (
        <Popup offset={[-5, -20]} className="custom-popup">
          <Styled.ContainerPopup>
            <Styled.CustomCloseButton onClick={handleClose}>
              <FuelIcon data-testid="close-icon" name="close" size="small" />
            </Styled.CustomCloseButton>
            <Styled.PopupContent>
              <Styled.HeaderContent>
                <FuelIcon
                  data-testid="alert-icon"
                  name="alert"
                  iconStyle="filled"
                  className="icon-alert"
                  size="small"
                />
                <FuelTypography
                  data-testid="title-popup"
                  size="body-md"
                  weight="bold"
                  color="neutral-strong"
                >
                  {label}!
                </FuelTypography>
              </Styled.HeaderContent>

              <Styled.PopupDescription data-testid="description-popup">
                {`"${description}"`}
              </Styled.PopupDescription>
              <FuelButton
                data-testid="button-resolved-popup"
                variant="secondary"
                size="small"
                fullWidth
                onClick={() => handleOccurrenceResolved()}
              >
                Resolvido
              </FuelButton>
            </Styled.PopupContent>
          </Styled.ContainerPopup>
        </Popup>
      )}
      {read && (
        <Tooltip direction="top" offset={[45, -50]}>
          <Styled.ToolTipContainer data-testid="tooltip">
            <Styled.ToolTipDescription>
              Ocorrência resolvida
            </Styled.ToolTipDescription>
          </Styled.ToolTipContainer>
        </Tooltip>
      )}
    </Marker>
  );
}

function LocationMarkers({
  origin,
  journeyId,
  destination,
  lastLocation,
  occurrencesLocation,
}: MarkerInterface) {
  return (
    <>
      <MarkerWithPopup
        journeyId={journeyId}
        position={origin}
        icon={iconsLocations.origin}
      />
      <MarkerWithPopup
        position={lastLocation}
        icon={iconsLocations.lastLocation}
        journeyId={journeyId}
      />
      <MarkerWithPopup
        position={destination}
        icon={iconsLocations.destination}
        journeyId={journeyId}
      />
      {occurrencesLocation.map((occurrence) => (
        <MarkerWithPopup
          key={occurrence.id}
          position={occurrence.location}
          icon={
            occurrence.read
              ? iconsLocations.occurrenceResolved
              : iconsLocations.occurrence
          }
          label="Ocorrência"
          description={occurrence.observations}
          read={occurrence.read}
          occurrenceId={occurrence.id}
          journeyId={journeyId}
        />
      ))}
    </>
  );
}

export default LocationMarkers;
